// store/logbooks.ts
import { defineStore } from "pinia";
import LogbookService from "@/services/LogbookService";
import LogbookTemplate from "@/models/LogbookTemplate";
import { LOGBOOK_TEMPLATE_NAMES } from "@/constants";
import type { LogbookType, DuplicateLogbookType } from "@/types";
import { captureException } from "@/utils/sentry";
import { useLoggerStore } from "@/stores/LoggerStore";

export const useLogbookStore = defineStore("logbooks", {
  state: () => ({
    logbooks: [] as LogbookType[],
    customLogbookTemplates: [] as LogbookTemplate[],
    // Logbooks-list filters. Empty values mean "no constraint". These are
    // session-only -- intentionally not persisted across app restarts.
    searchQuery: "",
    // Selected template keys (multi-select). Empty array means "all templates".
    templateFilter: [] as string[],
  }),
  getters: {
    getLogbookTemplateById: (state) => {
      return (id: string): LogbookTemplate | undefined =>
        state.customLogbookTemplates.find(template => template._id === id);
    },

    // Human-readable template name for a logbook, matching what the list row
    // displays: the custom template's label for CUSTOM logbooks, otherwise the
    // built-in template name.
    logbookTemplateName: (state) => {
      return (logbook: LogbookType): string => {
        if (logbook.template === "CUSTOM") {
          const custom = logbook.templateId
            ? state.customLogbookTemplates.find(t => t._id === logbook.templateId)
            : undefined;
          return custom?.label || "UNAVAILABLE";
        }
        // A malformed/legacy logbook can be missing `template` entirely. Always
        // return a string so consumers (the sort in availableTemplates, the
        // search haystack) never blow up on an undefined label.
        return LOGBOOK_TEMPLATE_NAMES[logbook.template] ?? logbook.template ?? "UNKNOWN";
      };
    },

    // Stable key identifying a logbook's template. Distinct custom templates get
    // distinct keys so they each appear as a separate option in the filter.
    logbookTemplateKey: () => {
      return (logbook: LogbookType): string =>
        logbook.template === "CUSTOM" && logbook.templateId
          ? `CUSTOM:${logbook.templateId}`
          : logbook.template;
    },

    // Distinct templates represented by the user's logbooks, for the filter
    // dropdown -- each with a count of how many logbooks use it. Sorted by
    // display name.
    availableTemplates(state): { value: string; label: string; count: number }[] {
      const byKey = new Map<string, { label: string; count: number }>();
      for (const logbook of state.logbooks) {
        const key = this.logbookTemplateKey(logbook);
        const existing = byKey.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          byKey.set(key, { label: this.logbookTemplateName(logbook), count: 1 });
        }
      }
      return Array.from(byKey, ([value, { label, count }]) => ({ value, label, count })).sort(
        (a, b) => a.label.localeCompare(b.label),
      );
    },

    // Logbooks after applying the active template filter and the text search.
    // The two controls combine: a logbook must satisfy both. The text search
    // matches a logbook's title or its template name (case-insensitive
    // substring), so typing what you see in the list -- the logbook's name --
    // works as expected, and template names like "POTA" still match too.
    filteredLogbooks(state): LogbookType[] {
      const query = state.searchQuery.trim().toLowerCase();
      return state.logbooks.filter(logbook => {
        if (
          state.templateFilter.length &&
          !state.templateFilter.includes(this.logbookTemplateKey(logbook))
        ) {
          return false;
        }
        if (query) {
          const haystack =
            `${logbook.title} ${this.logbookTemplateName(logbook)}`.toLowerCase();
          if (!haystack.includes(query)) return false;
        }
        return true;
      });
    },
  },
  actions: {
    setSearchQuery(query: string) {
      this.searchQuery = query;
    },

    setTemplateFilter(templateKeys: string[]) {
      this.templateFilter = templateKeys;
    },

    resetFilters() {
      this.searchQuery = "";
      this.templateFilter = [];
    },

    async fetchLogbookTemplates() {
      try {
        const templates = await LogbookTemplate.findAll();
        this.customLogbookTemplates = templates;
      } catch (error) {
        // Previously swallowed (console only). Report it: a failure here blanks
        // the list/filter UI with no signal, so we never learned how many users
        // were affected. The wrapper is fail-silent and never throws.
        console.error("Error fetching logbook templates:", error);
        captureException(error, { scope: "LogbookStore.fetchLogbookTemplates" });
      }
    },
    async fetchLogbooks() {
      try {
        const logbooks = await LogbookService.findAll();
        this.logbooks = logbooks;
      } catch (error) {
        // Previously swallowed (console only) -- this is exactly why the
        // null-createdAt list-blanking bug was invisible in Sentry. Report it so
        // the real blast radius is observable. The wrapper never throws.
        console.error("Error fetching logbooks:", error);
        captureException(error, { scope: "LogbookStore.fetchLogbooks" });
      }
    },

    async createLogbook(newLogbookData: LogbookType) {
      try {
        const createdLogbook = await LogbookService.create({ attrs: newLogbookData });
        await this.fetchLogbooks();
        return createdLogbook;
      } catch (error) {
        console.error("Error creating logbook:", error);
        throw error;
      }
    },

    async updateLogbook(logbook: LogbookType) {
      try {
        const createdLogbook = await LogbookService.update(logbook);
        await this.fetchLogbooks();
        return createdLogbook;
      } catch (error) {
        console.error("Error updating logbook:", error);
        throw error;
      }
    },

    async deleteLogbook(id: string) {
      try {
        await LogbookService.delete(id);
        // If this logbook is loaded in the logger, tear it down so no
        // post-delete sticky-field write fires against the now-deleted id
        // (which would resurrect a corrupt shell logbook). No-op otherwise.
        useLoggerStore().handleLogbookDeleted(id);
        await this.fetchLogbooks();
        return;
      } catch (error) {
        console.error("Error deleting logbook:", error);
        throw error;
      }
    },

    async duplicateLogbook(attrs: DuplicateLogbookType) {
      try {
        await LogbookService.duplicate(attrs);
        await this.fetchLogbooks();
        return;
      } catch (error) {
        console.error("Error duplicating logbook:", error);
        throw error;
      }
    }
  },
});
